import { useEffect, useMemo } from 'react';
import * as c3 from 'c3';
import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import { EmotionIcon } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import { renderToString } from 'react-dom/server';
import { readableMonth } from '@hotjar/webapp/lib/time';
import { FeedbackSentimentGraphTooltip } from '@hotjar/webapp/features/Feedback/SentimentGraph/SentimentGraphTooltip';
import styled from '@emotion/styled';
import { roundNumber } from '@hotjar/insights-common/lib/number';
import { EmotionScores } from '@hotjar/insights-common/constants/emotions';

const S = {
  Container: styled.div`
    display: flex;
    margin: 0 30px 30px;
  `,
  XAxis: styled.span<{ emotionStyle: EmotionStyle }>`
    display: flex;
    flex-direction: column;
    padding-top: ${(p) => (p.emotionStyle === 'star' ? 24 : 22)}px;
  `,
  EmotionIcon: styled(EmotionIcon)<{ emotionStyle: EmotionStyle }>`
    display: flex;
    justify-content: flex-end;
    flex: 0;
    &:not(:last-of-type) {
      margin-bottom: ${(p) => (p.emotionStyle === 'star' ? 48 : 42)}px;
    }
  }`,
};

export interface FeedbackResponsesSentimentGraphProps {
  averageDailySentiment: Record<string, number>;
  emotionStyle: EmotionStyle;
  dailyResponseCount: Record<string, number>;
  graphId?: string;
}

export function FeedbackSentimentGraph({
  averageDailySentiment,
  emotionStyle,
  dailyResponseCount,
  graphId = 'sentiment-graph',
}: FeedbackResponsesSentimentGraphProps): JSX.Element {
  const chartData = useMemo(() => {
    const date = new Date(Date.now());
    const dateStrings = [];
    const averageSentiments = [];
    // get oldest sample date
    const minDate = Math.min(
      ...Object.keys(averageDailySentiment).map((timestamp) => Number(timestamp)),
    );
    // get number of days between today and oldest sample to draw a chart
    // we want to generate day - sentiment value pairs for each day between these dates
    // if there is no sentiment value for specific day, chart will interpolate the line
    const numberOfSamples = (date.getTime() / 1000 - minDate) / 86400;

    // Prepare data - one list of epoch dates and one list of average sentiment for those dates.
    for (let i = 0; i < numberOfSamples; i += 1) {
      // We always reset date to midnight to compensate for daylight savings changes during the period.
      date.setUTCHours(0, 0, 0, 0);
      const averageSentiment = averageDailySentiment[date.getTime() / 1000];
      averageSentiments.push(
        averageSentiment === undefined ? null : roundNumber(averageSentiment, 2).toString(),
      );

      // C3 doesn't accept dates with time + timezone.
      dateStrings.push(date.toISOString().split('T')[0]);
      date.setDate(date.getDate() - 1);
    }
    return { dateStrings, averageSentiments };
    // this is intentional - we want to refresh calculations only when the averageDailySentiment changes
    // in order to avoid multiple chart redraws when date filter changes and the data for it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [averageDailySentiment]);

  useEffect(() => {
    if (!chartData?.averageSentiments) return;

    c3.generate({
      axis: {
        x: {
          type: 'timeseries',
          localtime: false,
          tick: {
            format: (d) =>
              `${readableMonth((d as Date).getMonth(), true)} ${(d as Date).getDate()}`,
          },
        },
        y: {
          max: 4,
          min: 0,
          padding: {
            bottom: 0,
          },
          tick: {
            format: () => '',
          },
        },
      },
      size: {
        height: 316,
      },
      bindto: `#${graphId}`,
      data: {
        x: 'x',
        columns: [
          ['x'].concat(chartData.dateStrings),
          ['Average sentiment'].concat(chartData.averageSentiments as string[]),
        ],
        types: {
          'Average sentiment': 'area',
        },
      },
      grid: {
        y: {
          show: true,
        },
      },
      legend: {
        show: false,
      },
      padding: {
        left: 16,
        right: 16,
        bottom: 16,
      },
      line: {
        connectNull: true,
      },
      tooltip: {
        grouped: true,
        contents(d) {
          // We need to be able to match the values we get from the database.
          // Sample date is in UTC and new Date() would be in local time.
          // Get time and subtract from it the current timezoneoffset so we always get UTC time.
          const sampleDate = d[0].x;
          const dateWithoutTimezone =
            sampleDate.getTime() / 1000 - sampleDate.getTimezoneOffset() * 60;
          const totalResponses = dailyResponseCount[dateWithoutTimezone];

          return renderToString(
            <FeedbackSentimentGraphTooltip
              emotionStyle={emotionStyle}
              score={d[0].value}
              totalResponses={totalResponses}
              sampleDate={sampleDate}
            />,
          );
        },
      },
    });
  }, [averageDailySentiment, chartData, emotionStyle, dailyResponseCount, graphId]);

  return (
    <S.Container>
      <S.XAxis emotionStyle={emotionStyle}>
        <>
          {EmotionScores.slice()
            .reverse()
            .map((s) => (
              <S.EmotionIcon
                key={s}
                emotionStyle={emotionStyle}
                score={s}
                compactStars
                hideOffStars
                size={emotionStyle === 'star' ? 'xs' : 's'}
              />
            ))}
        </>
      </S.XAxis>
      <div id={graphId} />
    </S.Container>
  );
}
